import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from "react";
import axios from "axios";
import { AuthContext } from "@/App";
import { useTableDensity } from "@/hooks/useTableDensity";
import { fmtDateShort } from "@/components/dashboard/utils";

// Stores is rupee-native: raw material is bought here, in ₹, from Indian
// suppliers. The ERP-wide formatCurrency stays USD for everything else —
// this one is deliberately local so nothing outside Stores changes currency.
const formatINR = (val) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0, maximumFractionDigits: 0 })
    .format(Number(val) || 0);

// The backend hands over unit_cost_inr ready-made; the fallbacks are only for
// a lot that predates the currency stamp.
const lotCostInr = (lot) => {
  if (lot?.unit_cost_inr != null) return Number(lot.unit_cost_inr) || 0;
  const raw = Number(lot?.unit_cost) || 0;
  if (lot?.unit_cost_currency === "USD") return raw / (Number(lot?.fx_inr_usd) || 0.012);
  return raw;
};
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "@/components/ui/sonner";
import { ReceiveStockModal, ReserveModal, IssueModal, AdjustModal, ScrapLotModal, EditLotModal, LotNotesDialog, StoresImportModal, SHAPES } from "@/components/StoresModals";
import PageTitle from "@/components/PageTitle";
import { LiveWake } from "@/components/LiveIndicator";
import {
  Warehouse, Plus, Upload, Search, Recycle, Eye, StickyNote,
  FileCheck2, FileWarning, PackagePlus, ArrowDownToLine, Wrench,
  SlidersHorizontal, History, Loader2, Pencil, Trash2, FileText, ExternalLink, Download, Link2Off,
  Copy, MapPin, Tag, Shapes, X, ChevronDown, Sheet, Filter, Undo2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Page sizes for the footer pagination — same options as the stage pages.
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

// ── Notion-style status pills ──
const STATUS_STYLE = {
  Available: { dot: "#16a34a", cls: "bg-emerald-500/12 text-emerald-700 dark:text-emerald-300" },
  Reserved:  { dot: "#2563eb", cls: "bg-blue-500/12 text-blue-700 dark:text-blue-300" },
  Low:       { dot: "#ca8a04", cls: "bg-amber-500/12 text-amber-700 dark:text-amber-300" },
  Depleted:  { dot: "#a1a1aa", cls: "bg-zinc-500/14 text-zinc-600 dark:text-zinc-400" },
  Scrapped:  { dot: "#dc2626", cls: "bg-red-500/12 text-red-700 dark:text-red-300" },
};

// ── Activity ledger visuals ──
const TXN_VISUAL = {
  receive:   { icon: PackagePlus,   cls: "bg-orange-500/12 text-orange-700 dark:text-orange-300", label: "Received" },
  remnant:   { icon: Recycle,         cls: "bg-orange-500/12 text-orange-700 dark:text-orange-300", label: "Remnant logged" },
  reserve:   { icon: ArrowDownToLine, cls: "bg-blue-500/12 text-blue-700 dark:text-blue-300",       label: "Reserved" },
  unreserve: { icon: ArrowDownToLine, cls: "bg-zinc-500/14 text-zinc-600 dark:text-zinc-400",       label: "Released" },
  issue:     { icon: Wrench,          cls: "bg-emerald-500/12 text-emerald-700 dark:text-emerald-300", label: "Issued" },
  adjust:    { icon: SlidersHorizontal, cls: "bg-amber-500/12 text-amber-700 dark:text-amber-300",  label: "Adjusted" },
  scrap:     { icon: Trash2,           cls: "bg-red-500/12 text-red-700 dark:text-red-300",       label: "Scrapped" },
  unscrap:   { icon: Undo2,            cls: "bg-zinc-500/14 text-zinc-600 dark:text-zinc-400",     label: "Scrap reversed" },
};

function timeAgo(ts) {
  if (!ts) return "";
  const s = (Date.now() - new Date(ts)) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return fmtDateShort(ts);
}

function shortName(u) {
  if (!u) return "—";
  return u.replace(/\s*\(.*\)/, "").trim().split(" ")[0];
}

// Export the currently-filtered stock view to CSV. Mirrors exportStageView:
// CSV with a UTF-8 BOM so Excel renders Ø / × / ″ correctly, date-stamped file.
function exportStockCSV(lots, canViewCost) {
  const q = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const cols = [
    ["Material", (l) => l.material],
    ["Grade", (l) => l.grade],
    ["Spec / standard", (l) => l.spec],
    ["Shape", (l) => l.shape],
    ["Bar / tag no", (l) => l.bar_no],
    ["Customer", (l) => l.customer],
    ["Heat #", (l) => l.heat_number],
    ["MTC on file", (l) => (l.mtc_on_file ? "Yes" : "No")],
    ["MTC link", (l) => l.mtc_link],
    ["Dimensions", (l) => l.dimensions],
    ["Unit", (l) => l.unit],
    ["On hand", (l) => l.quantity_on_hand ?? 0],
    ["Reserved", (l) => l.quantity_reserved ?? 0],
    ["Free", (l) => Math.max(0, (l.quantity_on_hand || 0) - (l.quantity_reserved || 0))],
    ...(canViewCost ? [["Unit cost (INR)", (l) => (l.pre_expensed ? 0 : lotCostInr(l))]] : []),
    ["Supplier", (l) => l.supplier],
    ["Location", (l) => l.location],
    ["Remnant", (l) => (l.is_remnant ? "Yes" : "No")],
    ["Source PO", (l) => l.source_po],
    ["Status", (l) => l.status],
    ["Received", (l) => l.received_at],
  ];
  const lines = [cols.map((c) => q(c[0])).join(",")];
  lots.forEach((l) => lines.push(cols.map((c) => q(c[1](l))).join(",")));
  const now = new Date();
  const mo = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const fname = `rocklink_stores_${String(now.getDate()).padStart(2, "0")}-${mo[now.getMonth()]}-${String(now.getFullYear()).slice(-2)}.csv`;
  const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = fname; a.click();
  URL.revokeObjectURL(url);
}

function ActionBtn({ icon: Icon, children, onClick, disabled, danger }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className={`inline-flex items-center gap-2 text-[13px] font-medium px-3.5 py-2 rounded-lg border transition-colors active:scale-[0.98] disabled:opacity-40 ${danger ? "border-red-500/30 text-red-600 hover:bg-red-500/10" : "border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/60"}`}>
      <Icon className="w-4 h-4" /> {children}
    </button>
  );
}

// ── Stats-strip pieces (mirrors the stage pages' StatChip pattern) ──
// StoreStat is a read-only figure; StoreChip doubles as a filter toggle so the
// same control is never duplicated elsewhere on the page.
function StoreStat({ label, value, color }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-[hsl(var(--muted-foreground))] select-none">
      <span className="font-bold tabular-nums font-mono-data" style={color ? { color } : undefined}>{value ?? "—"}</span>
      <span className="opacity-80">{label}</span>
    </span>
  );
}

function StoreChip({ count, label, color, active, onClick, title, testId }) {
  return (
    <button
      type="button" onClick={onClick} title={title}
      className="group/sc inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] transition-all duration-150 cursor-pointer select-none focus:outline-none focus-visible:ring-2"
      style={{
        background: active ? `${color}18` : "transparent",
        color:      active ? color        : "hsl(var(--muted-foreground))",
      }}
      onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = `${color}0d`; e.currentTarget.style.color = color; } }}
      onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "hsl(var(--muted-foreground))"; } }}
      data-testid={testId}
    >
      <span className="font-bold tabular-nums font-mono-data">{count}</span>
      <span className="opacity-80 group-hover/sc:opacity-100 transition-opacity">{label}</span>
      <Filter
        className="w-2.5 h-2.5 transition-all duration-150"
        style={{
          opacity: active ? 1 : 0,
          transform: active ? "translateX(0) scale(1)" : "translateX(-4px) scale(0.7)",
          marginLeft: active ? "2px" : "0",
        }}
      />
    </button>
  );
}

export default function StoresPage() {
  const { user } = useContext(AuthContext);
  const { density } = useTableDensity();
  const isCompact = density === "compact";

  const perms = user?.permissions || {};
  const isAdmin = user?.role === "Admin";
  const canManage = isAdmin || !!perms.can_manage_stock;
  const canViewCost = isAdmin || !!perms.can_view_stock_cost;
  const canReserve = isAdmin || !!perms.can_reserve_stock;
  const canIssue = isAdmin || !!perms.can_issue_stock;
  const canExport = isAdmin || !!perms.can_export;
  const hasRowActions = canReserve || canIssue || canManage;

  const [lots, setLots] = useState([]);
  const [summary, setSummary] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [remnantsOnly, setRemnantsOnly] = useState(false);
  const [expanded, setExpanded] = useState({});       // lot_id → bool (spec details)
  const [historyLot, setHistoryLot] = useState(null);
  const [historyTxns, setHistoryTxns] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [modal, setModal] = useState(null);        // { type, lot }
  const [notesLot, setNotesLot] = useState(null);
  const [editLoc, setEditLoc] = useState(null);     // lot id whose location is being edited
  const [locDraft, setLocDraft] = useState("");
  const [selected, setSelected] = useState(() => new Set());  // bulk selection (lot ids)
  const [shapeFilter, setShapeFilter] = useState("");
  const [showDepleted, setShowDepleted] = useState(false);    // hidden by default
  const [showScrapped, setShowScrapped] = useState(false);    // scrapped lots hidden by default
  const [page, setPage] = useState(1);                        // footer pagination
  const [pageSize, setPageSize] = useState(100);              // 0 = All
  const [bulkField, setBulkField] = useState(null);           // "location" | "customer" | "shape"
  const [bulkValue, setBulkValue] = useState("");
  const qDebounce = useRef(null);

  const fetchLots = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (remnantsOnly) params.set("remnants_only", "true");
      // Pull scrapped lots into the page too when the Scrapped filter is on, so
      // they can be opened and un-scrapped (they're excluded server-side by default).
      if (showScrapped) params.set("include_scrapped", "true");
      const res = await axios.get(`${API}/stores/lots?${params.toString()}`);
      setLots(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setLots([]);
    } finally {
      setLoading(false);
    }
  }, [q, remnantsOnly, showScrapped]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stores/summary`);
      setSummary(res.data || null);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchActivity = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stores/activity?limit=40`);
      setActivity(Array.isArray(res.data) ? res.data : []);
    } catch (e) { /* ignore */ }
  }, []);

  // Debounced lots fetch on filter/search change.
  useEffect(() => {
    if (qDebounce.current) clearTimeout(qDebounce.current);
    qDebounce.current = setTimeout(fetchLots, 220);
    return () => qDebounce.current && clearTimeout(qDebounce.current);
  }, [fetchLots]);

  useEffect(() => {
    fetchSummary();
    fetchActivity();
    const id = setInterval(fetchActivity, 15000);
    return () => clearInterval(id);
  }, [fetchSummary, fetchActivity]);

  const openHistory = useCallback(async (lot) => {
    setHistoryLot(lot);
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/stores/lots/${lot.id}/transactions`);
      setHistoryTxns(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setHistoryTxns([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const refreshAll = useCallback(() => { fetchLots(); fetchSummary(); fetchActivity(); }, [fetchLots, fetchSummary, fetchActivity]);

  const saveLocation = useCallback(async (lot) => {
    const v = locDraft.trim();
    setEditLoc(null);
    if (v === (lot.location || "")) return;
    try {
      await axios.patch(`${API}/stores/lots/${lot.id}`, { location: v });
      toast.success("Location updated");
      refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update location"); }
  }, [locDraft, refreshAll]);

  const deleteLot = useCallback(async (lot) => {
    if (!window.confirm(`Delete this lot (${lot.material}${lot.grade ? " · " + lot.grade : ""})? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/stores/lots/${lot.id}`);
      toast.success("Lot deleted");
      refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  }, [refreshAll]);

  const unscrapLot = useCallback(async (lot) => {
    if (!window.confirm(`Reverse the scrap on this lot (${lot.material}${lot.grade ? " · " + lot.grade : ""})? It will return to the store with ${lot.pre_scrap_on_hand || 0} ${lot.unit}.`)) return;
    try {
      await axios.post(`${API}/stores/lots/${lot.id}/unscrap`);
      toast.success("Scrap reversed");
      refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to reverse scrap"); }
  }, [refreshAll]);

  // ── Client-side view filters ──
  // Scrapped is an exclusive view: ON → show ONLY scrapped lots (so they can be
  // opened and un-scrapped); OFF → never show scrapped lots in the normal/depleted
  // views (a scrapped lot has 0 on-hand and would otherwise leak into "depleted").
  const visibleLots = useMemo(() => {
    if (showScrapped) {
      return lots.filter((l) => l.is_scrapped && (!shapeFilter || l.shape === shapeFilter));
    }
    return lots.filter((l) =>
      !l.is_scrapped &&
      (showDepleted || (l.quantity_on_hand || 0) > 0) &&
      (!shapeFilter || l.shape === shapeFilter)
    );
  }, [lots, showDepleted, showScrapped, shapeFilter]);
  const depletedCount = useMemo(() => lots.filter((l) => !l.is_scrapped && (l.quantity_on_hand || 0) <= 0).length, [lots]);

  // ── Shape browser counts (left rail — the old Excel's per-shape sheets) ──
  // Counts respect the depleted toggle but NOT the shape filter itself, so the
  // list always shows what clicking each shape would yield.
  const shapeCounts = useMemo(() => {
    const m = new Map();
    lots.forEach((l) => {
      if (l.is_scrapped) return;
      if (!showDepleted && (l.quantity_on_hand || 0) <= 0) return;
      const s = (l.shape || "").trim();
      if (s) m.set(s, (m.get(s) || 0) + 1);
    });
    return m;
  }, [lots, showDepleted]);
  const shapeList = useMemo(() => {
    const known = SHAPES.filter((s) => shapeCounts.has(s));
    const extras = [...shapeCounts.keys()].filter((s) => !SHAPES.includes(s)).sort();
    const all = [...known, ...extras];
    if (shapeFilter && !all.includes(shapeFilter)) all.push(shapeFilter); // keep an emptied selection visible
    return all;
  }, [shapeCounts, shapeFilter]);
  const allShapeTotal = useMemo(
    () => lots.filter((l) => !l.is_scrapped && (showDepleted || (l.quantity_on_hand || 0) > 0)).length,
    [lots, showDepleted]
  );

  // ── Pagination (stage-style; ready for the 1,600+ lot migration) ──
  const totalPages = pageSize === 0 ? 1 : Math.max(1, Math.ceil(visibleLots.length / pageSize));
  const pageSafe = Math.min(page, totalPages);
  const paged = pageSize === 0 ? visibleLots : visibleLots.slice((pageSafe - 1) * pageSize, pageSafe * pageSize);
  const showingFrom = visibleLots.length === 0 ? 0 : pageSize === 0 ? 1 : (pageSafe - 1) * pageSize + 1;
  const showingTo = pageSize === 0 ? visibleLots.length : Math.min(visibleLots.length, pageSafe * pageSize);

  // Snap back to page 1 — and drop any selection — whenever the view changes,
  // so bulk actions can never silently apply to rows that are no longer shown.
  useEffect(() => { setPage(1); setSelected(new Set()); }, [q, remnantsOnly, shapeFilter, showDepleted, showScrapped, pageSize]);

  // Self-heal stuck exclusive filters: if a chip's backing set empties while its
  // view is active, the chip disappears — so turn the filter off to avoid stranding
  // the user on an empty, un-clearable view (e.g. un-scrapping the last scrapped lot).
  useEffect(() => {
    if (showScrapped && (summary?.scrapped ?? 0) === 0) setShowScrapped(false);
  }, [showScrapped, summary?.scrapped]);
  useEffect(() => {
    if (showDepleted && depletedCount === 0) setShowDepleted(false);
  }, [showDepleted, depletedCount]);

  // ── Bulk selection (Excel-style: tick rows, act on all of them) ──
  // The header checkbox selects the CURRENT PAGE; a "select all filtered" link
  // in the bulk bar extends to every page (Gmail pattern — dumb-proof).
  const canBulk = canManage || canExport;
  const toggleSel = useCallback((id) => setSelected((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; }), []);
  const allPageSelected = paged.length > 0 && paged.every((l) => selected.has(l.id));
  const toggleAll = () => setSelected((prev) => {
    const n = new Set(prev);
    paged.forEach((l) => (allPageSelected ? n.delete(l.id) : n.add(l.id)));
    return n;
  });
  const selectAllFiltered = () => setSelected(new Set(visibleLots.map((l) => l.id)));

  const bulkApply = async () => {
    if (!bulkField) return;
    try {
      const res = await axios.post(`${API}/stores/lots/bulk-update`, { ids: [...selected], changes: { [bulkField]: bulkValue } });
      toast.success(`Updated ${res.data?.updated ?? 0} lot(s)`);
      setBulkField(null); setBulkValue(""); setSelected(new Set()); refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk update failed"); }
  };
  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} selected lot(s)? Reserved lots are skipped automatically. This cannot be undone.`)) return;
    try {
      const res = await axios.post(`${API}/stores/lots/bulk-delete`, { ids: [...selected] });
      const d = res.data || {};
      toast.success(`Deleted ${d.deleted ?? 0} lot(s)${d.skipped_reserved ? ` · ${d.skipped_reserved} skipped (reserved)` : ""}`);
      setSelected(new Set()); refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk delete failed"); }
  };
  const exportSelected = () => exportStockCSV(lots.filter((l) => selected.has(l.id)), canViewCost);

  const baseCols = canViewCost
    ? "minmax(230px,1fr) 140px 118px 160px 110px 84px"
    : "minmax(230px,1fr) 140px 118px 160px 84px";
  const gridCols = canBulk ? `34px ${baseCols}` : baseCols;

  const rowPadY = isCompact ? "py-1.5" : "py-3";

  return (
    <div style={{ minHeight: "100%" }} data-testid="stores-page">

        {/* ── Layout: left rail (shapes + activity) + unified stores card ──
            No inner max-width wrapper: Layout's <main> already provides the
            1440px container, so the page sits flush with the stage pages.
            items-stretch + flex column rail = activity bottom edge aligns
            with the card bottom edge. */}
        <div className="flex flex-col lg:flex-row gap-4 items-stretch">

          {/* ── Left rail ── */}
          <div className="w-full lg:w-[250px] shrink-0 order-2 lg:order-1 flex flex-col gap-4">

            {/* Shape browser — the only place shapes are filtered (mirrors the
                per-shape sheets of the old Excel workbook) */}
            <div className="rl-glass-edge bg-[hsl(var(--card))] rounded-xl overflow-hidden shrink-0" data-testid="stores-shape-filter">
              <div className="flex items-center gap-2 px-4 pt-3.5 pb-2.5 border-b border-[hsl(var(--border))]/50">
                <Shapes className="w-4 h-4 text-orange-500" />
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))]">Shapes</span>
              </div>
              <div className="p-2">
                <button
                  onClick={() => setShapeFilter("")}
                  className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg text-[12px] font-medium transition-colors ${
                    !shapeFilter
                      ? "bg-orange-500/12 text-orange-600"
                      : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/50 hover:text-[hsl(var(--foreground))]"
                  }`}
                  data-testid="stores-shape-all"
                >
                  <span>All shapes</span>
                  <span className="font-mono-data text-[11px]">{allShapeTotal}</span>
                </button>
                {shapeList.map((s) => (
                  <button
                    key={s}
                    onClick={() => setShapeFilter(shapeFilter === s ? "" : s)}
                    className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg text-[12px] font-medium transition-colors ${
                      shapeFilter === s
                        ? "bg-orange-500/12 text-orange-600"
                        : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/50 hover:text-[hsl(var(--foreground))]"
                    }`}
                    data-testid={`stores-shape-${s.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <span className="truncate text-left">{s}</span>
                    <span className="font-mono-data text-[11px]">{shapeCounts.get(s) || 0}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Activity rail — flex-fills the remaining rail height so its
                bottom edge matches the stores card; the list scrolls inside */}
            <div className="rl-glass-edge bg-[hsl(var(--card))] rounded-xl overflow-hidden flex-1 min-h-0 flex flex-col">
              <div className="flex items-center gap-2 px-4 pt-3.5 pb-2.5 border-b border-[hsl(var(--border))]/50 shrink-0">
                <History className="w-4 h-4 text-amber-500" />
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))]">Stores activity</span>
                <LiveWake className="ml-0.5" />
              </div>
              {/* Absolute inner scroller: contributes zero intrinsic height, so
                  the rail never grows taller than the stores card on desktop.
                  Mobile keeps a fixed 440px scroll window. */}
              <div className="relative flex-1 h-[440px] lg:h-auto lg:min-h-[160px]">
                <div className="absolute inset-0 overflow-y-auto">
                {activity.length === 0 ? (
                  <div className="text-center text-[11px] text-[hsl(var(--muted-foreground))] py-6">No activity yet</div>
                ) : activity.map((t) => {
                  const v = TXN_VISUAL[t.type] || TXN_VISUAL.adjust;
                  const Icon = v.icon;
                  const qty = `${(t.quantity || 0)} ${t.unit || ""}`.trim();
                  let sub = t.kitted_part
                    ? `kitted → ${t.kitted_part}${t.po_number ? ` · ${t.po_number}` : ""}`
                    : t.po_number ? (t.type === "reserve" ? `held for ${t.po_number}` : t.po_number)
                    : t.reason ? t.reason
                    : t.heat_number ? `heat ${t.heat_number}` : "";
                  if (t.ref_no) sub = sub ? `${sub} · MIS ${t.ref_no}` : `MIS ${t.ref_no}`;
                  return (
                    <div key={t.id} className="flex items-start gap-2.5 px-4 py-2.5 border-b border-[hsl(var(--border))]/30 last:border-0">
                      <span className={`shrink-0 w-[22px] h-[22px] rounded-md flex items-center justify-center ${v.cls}`}>
                        <Icon className="w-3.5 h-3.5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[12px] leading-tight">
                          <span className="font-medium">{v.label}</span> {qty} · {t.material}{t.grade ? ` ${t.grade}` : ""}
                        </div>
                        {sub && <div className="text-[10.5px] text-[hsl(var(--muted-foreground))] mt-0.5 font-mono-data truncate">{sub}</div>}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[11px] font-medium text-[hsl(var(--muted-foreground))]">{shortName(t.user)}</div>
                        <div className="text-[10.5px] text-[hsl(var(--muted-foreground))]/60 font-mono-data">{timeAgo(t.timestamp)}</div>
                      </div>
                    </div>
                  );
                })}
                </div>
              </div>
            </div>
          </div>

          {/* ── Unified stores card — mirrors the StagePage anatomy ── */}
          <div className="flex-1 min-w-0 w-full order-1 lg:order-2">
            <div className="rl-glass-edge rounded-xl overflow-hidden bg-[hsl(var(--card))]">
              <div className="h-[2px]" style={{ backgroundColor: "#f97316" }} />

              {/* Card header — title, search and actions live here, nowhere else */}
              <div className="flex flex-wrap items-center justify-between gap-y-2 gap-x-4 px-5 pt-4 pb-3">
                <div className="flex items-center gap-3 min-w-0">
                  <PageTitle color="#f97316" title="Stores" count={summary?.on_hand ?? "—"} unit="lots in storage">
                    {(q || remnantsOnly || shapeFilter || showDepleted || showScrapped) && (
                      <div className="text-sm text-[hsl(var(--muted-foreground))] mt-0.5">
                        <span className="text-xs"><span className="font-mono-data">{visibleLots.length}</span> shown</span>
                      </div>
                    )}
                  </PageTitle>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="group/search relative w-full sm:w-auto">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[hsl(var(--muted-foreground))] group-focus-within/search:text-orange-500 transition-colors" />
                    <input
                      type="text"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Search material, grade, heat #…"
                      className="h-8 pl-8 pr-8 text-xs rounded-full rl-search bg-[hsl(var(--muted))]/50 border border-transparent focus:bg-[hsl(var(--card))] focus:border-orange-500/50 text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/60 focus:outline-none w-full sm:w-60"
                      aria-label="Search stock"
                      data-testid="stores-search"
                    />
                    {q && (
                      <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 rounded" aria-label="Clear search">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {canManage && (
                    <button
                      onClick={() => setModal({ type: "import" })}
                      className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full rl-pill border border-orange-500/40 bg-orange-500/5 text-orange-500 hover:bg-orange-500/10 hover:border-orange-500/60 text-[10px] font-semibold uppercase tracking-[0.08em] transition-colors"
                      data-testid="stores-import-btn"
                    >
                      <Upload className="w-3.5 h-3.5" /> Import
                    </button>
                  )}
                  {canManage && (
                    <button
                      onClick={() => setModal({ type: "receive" })}
                      className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full rl-lit-btn bg-orange-600 hover:bg-orange-500 text-white text-[11px] font-semibold uppercase tracking-[0.05em] transition-colors"
                      data-testid="stores-receive-btn"
                    >
                      <Plus className="w-3.5 h-3.5" /> Receive Stock
                    </button>
                  )}
                </div>
              </div>

              {/* Stats strip — Remnants & Depleted double as filters (click = toggle),
                  exactly like status chips on the stage pages. No separate filter row. */}
              <div className="flex items-center gap-x-2 gap-y-2 flex-wrap px-5 py-3" data-testid="stores-stats-strip">
                <span className="hidden md:inline-block md:w-24 shrink-0 text-[10px] font-bold uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))]/80 select-none" aria-hidden="true">
                  Stock
                </span>
                <StoreStat label="on hand" value={summary?.on_hand} />
                <StoreStat label="available" value={summary?.available} color="#059669" />
                <StoreChip
                  count={summary?.remnants ?? 0} label="remnants" color="#ea580c"
                  active={remnantsOnly} onClick={() => setRemnantsOnly((v) => !v)}
                  title="Click to show only remnant lots"
                  testId="stores-remnants-filter"
                />
                {depletedCount > 0 && (
                  <StoreChip
                    count={depletedCount} label="depleted" color="#71717a"
                    active={showDepleted} onClick={() => { setShowDepleted((v) => !v); setShowScrapped(false); }}
                    title="Finished lots (0 on hand) are hidden by default — click to show them"
                    testId="stores-depleted-toggle"
                  />
                )}
                {(summary?.scrapped ?? 0) > 0 && (
                  <StoreChip
                    count={summary.scrapped} label="scrapped" color="#dc2626"
                    active={showScrapped} onClick={() => { setShowScrapped((v) => !v); setShowDepleted(false); }}
                    title="Disposed lots (sent for scrap / salvage) — click to show them and un-scrap if needed"
                    testId="stores-scrapped-toggle"
                  />
                )}
                {canViewCost && (
                  <StoreStat label="carrying value" value={summary ? formatINR(summary.carrying_value_inr ?? summary.carrying_value ?? 0) : "—"} />
                )}
              </div>

              {/* Bulk actions bar — appears when rows are ticked */}
              {selected.size > 0 && (
                <div className="flex items-center gap-2 flex-wrap px-5 py-2 border-t border-[hsl(var(--border))]/50 bg-orange-500/[0.04]" data-testid="stores-bulk-bar">
                  <span className="text-[12.5px] font-medium">{selected.size} selected</span>
                  {allPageSelected && selected.size < visibleLots.length && (
                    <button
                      onClick={selectAllFiltered}
                      className="text-[11.5px] font-medium text-orange-600 hover:underline"
                      data-testid="stores-select-all-filtered"
                    >
                      Select all {visibleLots.length} filtered
                    </button>
                  )}
                  <span className="text-[11px] text-[hsl(var(--muted-foreground))] hidden sm:inline">— changes apply to every ticked row</span>
                  <span className="flex-1" />
                  {canManage && <ActionBtn icon={MapPin} onClick={() => { setBulkValue(""); setBulkField("location"); }}>Set location</ActionBtn>}
                  {canManage && <ActionBtn icon={Tag} onClick={() => { setBulkValue(""); setBulkField("customer"); }}>Set customer</ActionBtn>}
                  {canManage && <ActionBtn icon={Shapes} onClick={() => { setBulkValue(""); setBulkField("shape"); }}>Set shape</ActionBtn>}
                  {canExport && <ActionBtn icon={Download} onClick={exportSelected}>Export</ActionBtn>}
                  {canManage && <ActionBtn danger icon={Trash2} onClick={bulkDelete}>Delete</ActionBtn>}
                  <ActionBtn icon={X} onClick={() => setSelected(new Set())}>Clear</ActionBtn>
                </div>
              )}

              <div className="overflow-x-auto">
                <div style={{ minWidth: canViewCost ? 850 : 770 }}>
                  {/* Header row */}
                  <div className="grid items-center rl-thead border-t border-b border-[hsl(var(--border))] text-xs uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))]"
                    style={{ gridTemplateColumns: gridCols }}>
                    {canBulk && (
                      <div className="pl-3 py-2.5 flex items-center">
                        <input type="checkbox" checked={allPageSelected} onChange={toggleAll}
                          title="Select every row on this page" className="w-3.5 h-3.5 accent-orange-600 cursor-pointer" />
                      </div>
                    )}
                    <div className="px-4 py-2.5">Material / grade</div>
                    <div className="px-4 py-2.5">
                      <span className="font-mono-data px-2 py-0.5 rounded-md bg-[hsl(var(--card))] border border-[hsl(var(--border))] rl-pill font-semibold text-[hsl(var(--foreground))]">Heat #</span>
                      <span className="ml-1.5">/ MTC</span>
                    </div>
                    <div className="px-4 py-2.5">Dimensions</div>
                    <div className="px-4 py-2.5">Stock</div>
                    {canViewCost && <div className="px-4 py-2.5 text-right" title="Carried in rupees — the currency the material was bought in">Unit cost <span className="font-normal opacity-60">(₹)</span></div>}
                    <div className="px-4 py-2.5" />
                  </div>

                  {loading ? (
                    <div className="flex items-center justify-center py-12 text-[hsl(var(--muted-foreground))] text-sm gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading stock…
                    </div>
                  ) : visibleLots.length === 0 ? (
                    <div className="flex items-center justify-center py-12 text-[hsl(var(--muted-foreground))] text-sm">
                      {showScrapped ? "No scrapped lots" : `No stock lots ${q || remnantsOnly || shapeFilter ? "match your filters" : "yet"}${!showDepleted && depletedCount > 0 ? " — depleted lots are hidden" : ""}`}.
                    </div>
                  ) : paged.map((lot) => {
                    const st = STATUS_STYLE[lot.status] || STATUS_STYLE.Available;
                    const free = Math.max(0, (lot.quantity_on_hand || 0) - (lot.quantity_reserved || 0));
                    const reserved = lot.quantity_reserved || 0;
                    const isExpanded = !!expanded[lot.id];
                    const depleted = (lot.quantity_on_hand || 0) <= 0;
                    return (
                      <div key={lot.id}
                        className={`grid border-b border-[hsl(var(--border))]/50 last:border-0 group/row hover:bg-[hsl(var(--muted))]/20 transition-colors ${lot.is_remnant ? "bg-orange-500/[0.045]" : ""} ${depleted ? "opacity-55" : ""}`}
                        style={{ gridTemplateColumns: gridCols }}
                        data-testid={`stock-lot-${lot.id}`}>

                        {canBulk && (
                          <div className={`pl-3 ${rowPadY} flex items-start`}>
                            <input type="checkbox" checked={selected.has(lot.id)} onChange={() => toggleSel(lot.id)}
                              className="w-3.5 h-3.5 accent-orange-600 cursor-pointer mt-0.5" />
                          </div>
                        )}

                        {/* Material / grade */}
                        <div className={`px-4 ${rowPadY}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-[hsl(var(--foreground))]">{lot.material}{lot.grade ? ` · ${lot.grade}` : ""}</span>
                            {lot.is_remnant && (
                              <span className="inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-wide bg-orange-500/15 text-orange-600 px-1.5 py-0.5 rounded">
                                <Recycle className="w-2.5 h-2.5" /> Remnant
                              </span>
                            )}
                            {(lot.spec || lot.location || lot.shape || lot.customer || lot.mtc_ref) && (
                              <button
                                onClick={() => setExpanded((p) => ({ ...p, [lot.id]: !p[lot.id] }))}
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-semibold text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))]/50 hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))] transition-colors"
                                title={isExpanded ? "Hide spec, bin & customer" : "Show spec, bin & customer"}
                              >
                                Details
                                <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                              </button>
                            )}
                          </div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                            {[lot.bar_no ? `tag ${lot.bar_no}` : "", lot.supplier, lot.received_at ? `recd ${fmtDateShort(lot.received_at)}` : "", lot.source_po ? `from ${lot.source_po}` : "", lot.customer ? `for ${lot.customer}` : ""].filter(Boolean).join(" · ")}
                          </div>
                          {isExpanded && (
                            <div className="mt-1.5 rounded-lg border border-[hsl(var(--border))]/60 bg-[hsl(var(--muted))]/30 px-3 py-2 text-xs text-[hsl(var(--muted-foreground))] flex flex-wrap gap-x-4 gap-y-1">
                              {lot.shape && <span><span className="opacity-60">Shape </span>{lot.shape}</span>}
                              {lot.spec && <span><span className="opacity-60">Spec </span>{lot.spec}</span>}
                              {lot.customer && <span><span className="opacity-60">Customer </span>{lot.customer}</span>}
                              <span className="inline-flex items-center gap-1">
                                <span className="opacity-60">Bin </span>
                                {editLoc === lot.id ? (
                                  <input autoFocus value={locDraft}
                                    onChange={(e) => setLocDraft(e.target.value)}
                                    onBlur={() => saveLocation(lot)}
                                    onKeyDown={(e) => { if (e.key === "Enter") saveLocation(lot); if (e.key === "Escape") setEditLoc(null); }}
                                    className="h-6 px-1.5 w-28 text-[11px] rounded border border-orange-500 bg-[hsl(var(--card))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500/30" />
                                ) : canManage ? (
                                  <button className="hover:text-orange-600 underline decoration-dotted decoration-[hsl(var(--muted-foreground))]/40"
                                    onClick={() => { setEditLoc(lot.id); setLocDraft(lot.location || ""); }}>
                                    {lot.location || "set bin"}
                                  </button>
                                ) : (lot.location || "—")}
                              </span>
                              {lot.mtc_ref && <span><span className="opacity-60">MTC </span>{lot.mtc_ref}</span>}
                            </div>
                          )}
                        </div>

                        {/* Heat / MTC — heat number as a Part#-style mono chip */}
                        <div className={`px-4 ${rowPadY}`}>
                          {lot.heat_number ? (
                            <span className={`font-mono-data ${isCompact ? "text-[11px]" : "text-[13px]"} px-2 py-0.5 rounded-md bg-[hsl(var(--card))] border border-[hsl(var(--border))] rl-pill text-[hsl(var(--foreground))] font-semibold inline-block max-w-full [overflow-wrap:anywhere]`}>{lot.heat_number}</span>
                          ) : (
                            <span className={`font-mono-data ${isCompact ? "text-[11px]" : "text-[13px]"} px-2 py-0.5 rounded-md border border-dashed border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] opacity-60 inline-block`}>No Heat #</span>
                          )}
                          {lot.mtc_link ? (
                            <a href={lot.mtc_link} target="_blank" rel="noopener noreferrer"
                              className="text-[10.5px] mt-1 inline-flex items-center gap-1 w-fit text-emerald-600 hover:underline whitespace-nowrap"
                              title="Open MTC document">
                              <FileCheck2 className="w-3 h-3 shrink-0" /> MTC on file <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                            </a>
                          ) : lot.mtc_on_file ? (
                            <div className="text-[10.5px] mt-1 inline-flex items-center gap-1 text-emerald-600 whitespace-nowrap"
                              title="MTC marked on file, but no document is linked — add one via Edit">
                              <FileCheck2 className="w-3 h-3 shrink-0" /> MTC on file
                              <Link2Off className="w-2.5 h-2.5 shrink-0 text-[hsl(var(--muted-foreground))]" />
                            </div>
                          ) : (
                            <div className="text-[10.5px] mt-1 inline-flex items-center gap-1 text-amber-600 whitespace-nowrap">
                              <FileWarning className="w-3 h-3 shrink-0" /> MTC missing
                            </div>
                          )}
                        </div>

                        {/* Dimensions */}
                        <div className={`px-4 ${rowPadY} font-mono-data ${isCompact ? "text-[11px]" : "text-[13px]"} text-[hsl(var(--foreground))]/85`}>{lot.dimensions || "—"}</div>

                        {/* Stock (available / reserved / status) */}
                        <div className={`px-4 ${rowPadY}`}>
                          <div className={`font-mono-data ${isCompact ? "text-[11px]" : "text-[13px]"} font-semibold`}>
                            {free.toLocaleString()} <span className="text-[hsl(var(--muted-foreground))] text-[11px] font-normal">{lot.unit}</span>
                            {!depleted && <span className="text-emerald-600 text-[11px] font-normal ml-1">free</span>}
                          </div>
                          {reserved > 0 && (
                            <div className="font-mono-data text-[11px] text-blue-600 mt-0.5">{reserved.toLocaleString()} {lot.unit} reserved</div>
                          )}
                          <div className="mt-1">
                            <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-md ${st.cls}`}>
                              <span className="w-1.5 h-1.5 rounded-full" style={{ background: st.dot }} />
                              {lot.status}
                            </span>
                          </div>
                        </div>

                        {/* Unit cost */}
                        {canViewCost && (
                          <div className={`px-4 ${rowPadY} text-right font-mono-data ${isCompact ? "text-[11px]" : "text-[13px]"} font-semibold ${lot.pre_expensed ? "text-emerald-600" : ""}`}>
                            {lot.pre_expensed ? "₹0" : formatINR(lotCostInr(lot))}
                            <div className="text-[10px] font-normal text-[hsl(var(--muted-foreground))]">{lot.pre_expensed ? "pre-expensed" : `/ ${lot.unit}`}</div>
                          </div>
                        )}

                        {/* Actions — mirrors StagePage: NotesCell-style note button + round eye */}
                        <div className={`px-3 ${rowPadY} flex items-center gap-1.5`}>
                          <TooltipProvider delayDuration={150} skipDelayDuration={0}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className={(lot.notes_count || 0) > 0
                                  ? "inline-flex"
                                  : "inline-flex opacity-0 pointer-events-none group-hover/row:opacity-100 group-hover/row:pointer-events-auto transition-opacity duration-150"}>
                                  <button
                                    type="button"
                                    onClick={() => setNotesLot(lot)}
                                    className={`relative inline-flex items-center justify-center w-[30px] h-[30px] rounded-full rl-well transition-colors ${
                                      (lot.notes_count || 0) === 0
                                        ? "text-[hsl(var(--muted-foreground))]/60 hover:text-[hsl(var(--foreground))]"
                                        : "text-amber-600 dark:text-amber-400"
                                    }`}
                                    aria-label={(lot.notes_count || 0) > 0 ? `${lot.notes_count} notes — click to view or add` : "Add a note"}
                                  >
                                    <StickyNote className="w-4 h-4" strokeWidth={(lot.notes_count || 0) === 0 ? 1.6 : 2} />
                                    {(lot.notes_count || 0) > 0 && (
                                      <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[14px] h-[14px] px-[3px] rounded-full text-white text-[8.5px] font-bold leading-none ring-[1.5px] ring-[hsl(var(--card))] bg-red-500">
                                        {lot.notes_count > 99 ? "99+" : lot.notes_count}
                                      </span>
                                    )}
                                  </button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" sideOffset={6}
                                className="bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] border border-[hsl(var(--border))] shadow-sm text-[10px] font-medium tracking-wide px-2 py-1">
                                {(lot.notes_count || 0) > 0 ? `${lot.notes_count} note${lot.notes_count === 1 ? "" : "s"}` : "Add a note"}
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => openHistory(lot)}
                                  className="inline-flex items-center justify-center w-[30px] h-[30px] rounded-full rl-eye-btn text-[hsl(var(--foreground))]/70 hover:text-[hsl(var(--foreground))] active:scale-95 opacity-0 pointer-events-none group-hover/row:opacity-100 group-hover/row:pointer-events-auto focus-visible:opacity-100 focus-visible:pointer-events-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/40"
                                  aria-label="Lot details, actions & history"
                                  data-testid={`stock-lot-history-${lot.id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top" sideOffset={6}
                                className="bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] border border-[hsl(var(--border))] shadow-sm text-[10px] font-medium tracking-wide px-2 py-1">
                                Details, actions &amp; history
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── Pagination + export footer — mirrors the stage pages ── */}
              {!loading && visibleLots.length > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-5 py-2.5 border-t border-[hsl(var(--border))]">
                  <span className="text-xs text-[hsl(var(--muted-foreground))] shrink-0">
                    Showing <span className="font-mono-data font-semibold text-[hsl(var(--foreground))]">{showingFrom}–{showingTo}</span> of <span className="font-mono-data font-semibold text-[hsl(var(--foreground))]">{visibleLots.length}</span> lots
                  </span>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    {canExport && (
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={() => {
                                exportStockCSV(visibleLots, canViewCost);
                                toast.success(`Exported ${visibleLots.length} lot${visibleLots.length === 1 ? "" : "s"}`);
                              }}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide rounded-md border border-teal-500/30 bg-teal-500/6 text-teal-700 dark:text-teal-300 hover:bg-teal-500/15 hover:border-teal-500/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/40"
                              data-testid="stores-export-btn"
                            >
                              <Sheet className="w-3.5 h-3.5" />
                              Export this view
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p className="text-[11px]">Download all {visibleLots.length} filtered {visibleLots.length === 1 ? "lot" : "lots"} as a spreadsheet (CSV)</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    <div className="flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))]">
                      <span>Per page:</span>
                      <div className="flex rounded-md overflow-hidden border border-[hsl(var(--border))]">
                        {PAGE_SIZE_OPTIONS.map((size) => (
                          <button key={size} onClick={() => { setPageSize(size); setPage(1); }}
                            className={`px-2 py-0.5 text-[10px] font-mono-data transition-colors ${pageSize === size ? "bg-[hsl(var(--foreground))] text-[hsl(var(--background))] font-semibold" : "bg-[hsl(var(--muted))]/40 hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}
                            data-testid={`stores-page-size-${size}`}
                          >{size}</button>
                        ))}
                        <button onClick={() => { setPageSize(0); setPage(1); }}
                          className={`px-2 py-0.5 text-[10px] transition-colors ${pageSize === 0 ? "bg-[hsl(var(--foreground))] text-[hsl(var(--background))] font-semibold" : "bg-[hsl(var(--muted))]/40 hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}
                          data-testid="stores-page-size-all"
                        >All</button>
                      </div>
                    </div>
                    {totalPages > 1 && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => setPage(1)} disabled={pageSafe === 1}
                          className="px-1.5 py-0.5 text-[10px] rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 hover:bg-[hsl(var(--muted))] disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-mono-data"
                          data-testid="stores-page-first">«</button>
                        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pageSafe === 1}
                          className="px-2 py-0.5 text-[10px] rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 hover:bg-[hsl(var(--muted))] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          data-testid="stores-page-prev">‹ Prev</button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter((p) => p === 1 || p === totalPages || Math.abs(p - pageSafe) <= 2)
                          .reduce((acc, p, idx, arr) => {
                            if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                            acc.push(p);
                            return acc;
                          }, [])
                          .map((p, i) => p === "..." ? (
                            <span key={`ellipsis-${i}`} className="px-1 text-xs text-[hsl(var(--muted-foreground))]">…</span>
                          ) : (
                            <button key={p} onClick={() => setPage(p)}
                              className={`w-6 h-6 text-[10px] rounded border transition-colors font-mono-data ${pageSafe === p ? "bg-[hsl(var(--foreground))] text-[hsl(var(--background))] border-[hsl(var(--foreground))] font-semibold" : "border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}
                              data-testid={`stores-page-${p}`}
                            >{p}</button>
                          ))}
                        <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={pageSafe === totalPages}
                          className="px-2 py-0.5 text-[10px] rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 hover:bg-[hsl(var(--muted))] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          data-testid="stores-page-next">Next ›</button>
                        <button onClick={() => setPage(totalPages)} disabled={pageSafe === totalPages}
                          className="px-1.5 py-0.5 text-[10px] rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 hover:bg-[hsl(var(--muted))] disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-mono-data"
                          data-testid="stores-page-last">»</button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

      {/* ── Lot history / details modal (eye button) ── */}
      <Dialog open={!!historyLot} onOpenChange={(o) => { if (!o) setHistoryLot(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Warehouse className="w-4 h-4 text-orange-500" />
              {historyLot ? `${historyLot.material}${historyLot.grade ? ` · ${historyLot.grade}` : ""}` : ""}
            </DialogTitle>
            <DialogDescription>
              {historyLot ? [
                historyLot.dimensions,
                historyLot.heat_number ? `Heat ${historyLot.heat_number}` : "",
                historyLot.location,
              ].filter(Boolean).join("  ·  ") : ""}
            </DialogDescription>
          </DialogHeader>

          {historyLot && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-[hsl(var(--muted-foreground))]">
                <span className={`inline-flex items-center gap-1 ${historyLot.mtc_on_file ? "text-emerald-600" : "text-amber-600"}`}>
                  {historyLot.mtc_on_file ? <FileCheck2 className="w-3.5 h-3.5" /> : <FileWarning className="w-3.5 h-3.5" />}
                  {historyLot.mtc_on_file ? "MTC on file" : "MTC missing"}
                  {historyLot.mtc_on_file && !historyLot.mtc_link && <span className="text-[hsl(var(--muted-foreground))] ml-1">· no link attached</span>}
                </span>
                {historyLot.mtc_link && (
                  <a href={historyLot.mtc_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-orange-600 hover:underline">
                    <FileText className="w-3.5 h-3.5" /> Open MTC <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {historyLot.supplier && <span>Supplier: {historyLot.supplier}</span>}
              </div>
              {hasRowActions && (
                historyLot.is_scrapped ? (
                  <div className="flex flex-wrap gap-2.5">
                    {canManage && <ActionBtn icon={Undo2} onClick={() => { const l = historyLot; setHistoryLot(null); unscrapLot(l); }}>Un-scrap</ActionBtn>}
                    {canManage && <ActionBtn danger icon={Trash2} onClick={() => { const l = historyLot; setHistoryLot(null); deleteLot(l); }}>Delete</ActionBtn>}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2.5">
                    {canReserve && <ActionBtn disabled={(historyLot.quantity_on_hand || 0) <= 0} icon={ArrowDownToLine} onClick={() => { const l = historyLot; setHistoryLot(null); setModal({ type: "reserve", lot: l }); }}>Reserve</ActionBtn>}
                    {canIssue && <ActionBtn disabled={(historyLot.quantity_on_hand || 0) <= 0} icon={Wrench} onClick={() => { const l = historyLot; setHistoryLot(null); setModal({ type: "issue", lot: l }); }}>Issue</ActionBtn>}
                    {canManage && <ActionBtn icon={SlidersHorizontal} onClick={() => { const l = historyLot; setHistoryLot(null); setModal({ type: "adjust", lot: l }); }}>Adjust</ActionBtn>}
                    {canManage && <ActionBtn icon={Pencil} onClick={() => { const l = historyLot; setHistoryLot(null); setModal({ type: "edit", lot: l }); }}>Edit</ActionBtn>}
                    {canManage && <ActionBtn icon={Copy} onClick={() => { const l = historyLot; setHistoryLot(null); setModal({ type: "receive", lot: l }); }}>Duplicate</ActionBtn>}
                    {canManage && <ActionBtn icon={Trash2} onClick={() => { const l = historyLot; setHistoryLot(null); setModal({ type: "scrap", lot: l }); }}>Scrap</ActionBtn>}
                    {canManage && <ActionBtn danger icon={Trash2} onClick={() => { const l = historyLot; setHistoryLot(null); deleteLot(l); }}>Delete</ActionBtn>}
                  </div>
                )
              )}
            </div>
          )}

          <div className="text-[10.5px] uppercase tracking-[0.06em] text-[hsl(var(--muted-foreground))] mt-1">Movement history</div>
          <div className="max-h-[44vh] overflow-y-auto -mx-1 px-1">
            {historyLoading ? (
              <div className="flex items-center justify-center py-8 text-sm text-[hsl(var(--muted-foreground))] gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading history…
              </div>
            ) : historyTxns.length === 0 ? (
              <div className="text-center py-8 text-sm text-[hsl(var(--muted-foreground))]">No movements recorded yet.</div>
            ) : (
              <ol className="relative border-l border-[hsl(var(--border))] ml-2">
                {historyTxns.map((t) => {
                  const v = TXN_VISUAL[t.type] || TXN_VISUAL.adjust;
                  return (
                    <li key={t.id} className="mb-3 ml-4">
                      <span className="absolute -left-[5px] w-2.5 h-2.5 rounded-full" style={{ background: STATUS_STYLE.Available.dot }} />
                      <div className="text-[13px]">
                        <span className="font-medium">{v.label}</span>{" "}
                        <span className="font-mono-data">{(t.quantity || 0)} {t.unit}</span>
                        {t.kitted_part && <span className="text-[hsl(var(--muted-foreground))]"> → kitted into {t.kitted_part}</span>}
                        {t.po_number && <span className="text-[hsl(var(--muted-foreground))]"> · {t.po_number}</span>}
                        {t.ref_no && <span className="text-[hsl(var(--muted-foreground))]"> · MIS {t.ref_no}</span>}
                      </div>
                      <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">
                        {shortName(t.user)} {t.user_role ? `(${t.user_role})` : ""} · {fmtDateShort(t.timestamp)}
                        {t.reason ? ` · ${t.reason}` : ""}{t.note ? ` · ${t.note}` : ""}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Bulk edit dialog ── */}
      {bulkField && (
        <Dialog open onOpenChange={(o) => { if (!o) { setBulkField(null); setBulkValue(""); } }}>
          <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {bulkField === "location" ? <MapPin className="w-4 h-4 text-orange-500" /> : bulkField === "customer" ? <Tag className="w-4 h-4 text-orange-500" /> : <Shapes className="w-4 h-4 text-orange-500" />}
                Set {bulkField} for {selected.size} lot{selected.size !== 1 ? "s" : ""}
              </DialogTitle>
              <DialogDescription>
                {bulkField === "location" ? "Every ticked lot moves to this location / bin." :
                  bulkField === "customer" ? "Tag every ticked lot to a customer — leave blank to mark as surplus." :
                  "Sets the shape on every ticked lot."}
              </DialogDescription>
            </DialogHeader>
            {bulkField === "shape" ? (
              <select value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}
                className="w-full h-9 px-3 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500">
                <option value="">— pick a shape —</option>
                {SHAPES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            ) : (
              <input autoFocus value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") bulkApply(); }}
                placeholder={bulkField === "location" ? "e.g. Central shed / Rack B-3" : "e.g. BAKER · blank = surplus"}
                className="w-full h-9 px-3 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500" />
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => { setBulkField(null); setBulkValue(""); }}
                className="text-[13px] font-medium px-4 py-2 rounded-lg bg-[hsl(var(--muted))] border border-[hsl(var(--border))] hover:bg-[hsl(var(--accent))] text-[hsl(var(--foreground))] transition-colors">Cancel</button>
              <button type="button" onClick={bulkApply}
                className="text-[13px] font-medium px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-500 text-white transition-colors">Apply to {selected.size}</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Write modals ── */}
      {modal?.type === "receive" && <ReceiveStockModal initial={modal.lot} onClose={() => setModal(null)} onDone={refreshAll} />}
      {modal?.type === "reserve" && <ReserveModal lot={modal.lot} onClose={() => setModal(null)} onDone={refreshAll} />}
      {modal?.type === "issue" && <IssueModal lot={modal.lot} onClose={() => setModal(null)} onDone={refreshAll} />}
      {modal?.type === "adjust" && <AdjustModal lot={modal.lot} onClose={() => setModal(null)} onDone={refreshAll} />}
      {modal?.type === "scrap" && <ScrapLotModal lot={modal.lot} onClose={() => setModal(null)} onDone={refreshAll} />}
      {modal?.type === "edit" && <EditLotModal lot={modal.lot} onClose={() => setModal(null)} onDone={refreshAll} />}
      {modal?.type === "import" && <StoresImportModal onClose={() => setModal(null)} onDone={refreshAll} />}
      {notesLot && <LotNotesDialog lot={notesLot} canEdit onClose={() => setNotesLot(null)} onDone={refreshAll} />}
    </div>
  );
}
